// Row grouping of the profit calculator and the payout the server sends for each row.

// ─── Числовые поля для суммирования ──────────────────────────────────────────
export const SUM_FIELDS = [
  'quantity',
  'return_qty',
  'retail_amount',
  'ppvz_sales_commission',
  'acquiring_fee',
  'delivery_rub',
  'penalty',
  'storage_fee',
  'additional_payment',
  'ppvz_reward',
  'ppvz_for_pay',
  'deduction',
  'acceptance',
  'rebill_logistic_cost',
  'cashback_amount',
  'cashback_discount',
  'cashback_commission_change',
  'payment_schedule',
  'installment_cofinancing_amount',
];

// Fields WB sends positive on return rows too: their sign comes from the document type.
// Charges (logistics, storage, acceptance, penalties, deductions) are summed as sent.
export const DOC_SIGNED_FIELDS = [
  'ppvz_sales_commission',
  'acquiring_fee',
  'ppvz_reward',
  'ppvz_for_pay',
  'cashback_amount',
  'cashback_discount',
  'cashback_commission_change',
];

// ─── Группировка карточек по артикулу ────────────────────────────────────────
export const groupByArticle = (cards) => {
  const map = new Map();
  for (const card of cards) {
    const key = payoutKey(card.nm_id, card.sa_name);
    if (!map.has(key)) {
      map.set(key, {
        nm_id: card.nm_id,
        sa_name: card.sa_name,
        brand_name: card.brand_name,
        subject_name: card.subject_name,
        ...Object.fromEntries(SUM_FIELDS.map((f) => [f, 0])),
      });
    }
    const group = map.get(key);
    const docType = (card.doc_type_name || '').trim().toLowerCase();
    const isSale = docType === 'продажа';
    const isReturn = docType === 'возврат';

    for (const f of SUM_FIELDS) {
      if (f === 'quantity') {
        // Считаем количество только из строк продаж
        if (isSale) group[f] += Number(card[f] || 0);
      } else if (f === 'return_qty') {
        // Считаем возвраты из строк возвратов (WB хранит кол-во в поле quantity)
        if (isReturn) group[f] += Number(card.quantity || 0);
      } else if (f === 'retail_amount') {
        // Оплатили покупатели — нетто: прибавляем на продаже, вычитаем на возврате
        if (isSale) group[f] += Number(card[f] || 0);
        else if (isReturn) group[f] -= Number(card[f] || 0);
      } else if (DOC_SIGNED_FIELDS.includes(f)) {
        // Как в справочнике WB и в аналитике: сумма × знак документа (возврат списывает)
        group[f] += (isReturn ? -1 : 1) * Number(card[f] || 0);
      } else {
        group[f] += Number(card[f] || 0);
      }
    }
  }
  return Array.from(map.values());
};

// ─── К выплате ───────────────────────────────────────────────────────────────
// «К выплате» is not computed here: the server sends it in total and per calculator row, by the
// same rules (ComputeWB) and over the same report rows as the dashboard and the master report.
// A row is an article (nm_id); rows without one are grouped by the seller's vendor code. The two
// kinds never share a key: an article 5 and a vendor code "5" are different rows, as on the server.
export const payoutKey = (nmId, vendorCode) => (Number(nmId) ? `nm:${Number(nmId)}` : `vc:${vendorCode || ''}`);

// Map: row key → payout of that row (₽). A row missing here has no payout (e.g. only rows in
// another currency or outside the reports the payout is built from).
export const payoutByGroup = (payout) => {
  const map = new Map();
  for (const g of payout?.groups || []) {
    const key = payoutKey(g.nm_id, g.vendor_code);
    map.set(key, (map.get(key) || 0) + Number(g.to_pay));
  }
  return map;
};

// Report fields an unrecognised operation can carry into the payout, as the seller knows them.
const PAYOUT_FIELD_LABELS = {
  for_pay: 'к перечислению',
  delivery_service: 'логистика',
  paid_storage: 'хранение',
  paid_acceptance: 'приёмка и обработка товара',
  penalty: 'штрафы',
  deduction: 'удержания',
  additional_payment: 'корректировка вознаграждения WB',
  cashback_amount: 'баллы лояльности',
  cashback_commission_change: 'комиссия программы лояльности',
  cashback_discount: 'компенсация скидки лояльности',
  payment_schedule: 'изменение срока перечисления',
};

// What keeps the payout from being final, as messages for the seller. fmt formats roubles.
export const payoutWarnings = (payout, fmt) => {
  if (!payout) return ['Сервер не вернул сумму к выплате — она не показывается.'];
  const d = payout.diagnostics || {};
  const out = [];
  if (!d.coverage_full) out.push('Финансовые отчёты WB загружены не за весь выбранный период — сумма к выплате неполная.');
  if (d.incomplete_reports > 0) out.push(`Отчётов за период, загруженных не полностью: ${d.incomplete_reports}. Сумма изменится после загрузки.`);
  if (d.other_currencies?.length) {
    const list = d.other_currencies.map((c) => `${c.currency}: ${c.rows} стр.`).join(', ');
    out.push(`Есть операции в другой валюте (${list}). В выплату в рублях они не входят.`);
  }
  if (d.unsigned_rows > 0) out.push(`Строк продажи или возврата без типа документа: ${d.unsigned_rows} (К перечислению ${fmt(d.unsigned_for_pay)}). Знак операции неизвестен — в выплату они не входят.`);
  if (d.unknown_rows > 0) {
    const fields = (d.unknown_fields || []).map((f) => PAYOUT_FIELD_LABELS[f] || f).join(', ');
    out.push(`Нераспознанных операций с деньгами в выплате: ${d.unknown_rows}${fields ? ` (${fields})` : ''}, всего в выплате ${fmt(d.unknown_to_pay)}. Учтены, но что это за операции, неизвестно — проверьте их в мастер-отчёте.`);
  }
  if (d.excluded_rows > 0) out.push(`Строк периода вне расчёта выплаты: ${d.excluded_rows} (К перечислению ${fmt(d.excluded_for_pay)}): дневной отчёт, уже перекрытый недельным, или строка без отчёта. В таблице они есть, в выплате — нет.`);
  if (d.groups_match === false) out.push('Сумма по строкам не совпала с общим итогом — сообщите в поддержку.');
  return out;
};
